/**
 * Hermes Worker API client.
 *
 * All endpoints return the standard envelope:
 *   { success: boolean, data?: T, message?: string }
 *
 * In dev, requests go through the Vite proxy (/api → localhost:8787, strip /api).
 * In production, same-origin calls to the Worker itself.
 *
 * Auth is cookie-based: the Worker sets `hermes_nostr_session` on login/generate
 * and the browser includes it automatically via `credentials: 'include'`.
 */

const BASE = import.meta.env.DEV ? '/api' : '';
const NSEC_STORAGE_KEY = 'hermes_nsec';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    throw new ApiError(
      err instanceof Error ? err.message : 'Network request failed',
      'NETWORK_ERROR',
    );
  }

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(
      `Server returned ${res.status} with non-JSON body`,
      'PARSE_ERROR',
      res.status,
    );
  }

  if (!res.ok || !body.success) {
    throw new ApiError(
      body.message || `Request failed (${res.status})`,
      undefined,
      res.status,
    );
  }

  return body.data as T;
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

// ─── Types ──────────────────────────────────────────────────────────────

export interface DeviceState {
  status?: string | null;
  chargingStatus?: string | null;
  chargerName?: string | null;
  active?: boolean;
  isOnline?: boolean | null;
  isCharging?: boolean;
  kwhDelivered?: number | null;
  cableLocked?: boolean | null;
  accessOpen?: boolean | null;
  costPerKWh?: number | null;
  currencyId?: string | null;
  costModel?: string | null;
  chargerOpMode?: number;
  lastTriggered?: string | null;
  lastAckReceivedAt?: string | null;
  lastError?: string | null;
  triggerCounter?: number;
  spotPrice?: { costPerKWh?: number; costModel?: string } | null;
  sessions?: Array<{
    sessionId?: number;
    chargerCode?: string;
    sessionDateLocal?: string;
    durationSeconds?: number;
    durationDisplay?: string;
    energyKwh?: number;
    priceTotal?: number;
  }> | null;
}

export interface DevicePayload {
  uuid: string;
  service_type: string;
  service_code: string;
  device_category?: string;
  pricing_unit?: string;
  action_mode?: string;
  has_sessions?: boolean;
  has_pricing?: boolean;
  plugin_metadata: Record<string, unknown>;
  state: DeviceState;
  identifying_string?: string;
  auth_fuse_blown?: boolean;
  last_auth_success?: string;
}

export interface GenerateIdentityResponse {
  nsec: string;
  pubkey: string;
  npub: string;
}

export interface NpubIdentity {
  pubkey: string;
  npub: string;
}

export interface DevicesListResponse {
  count: number;
  devices: DevicePayload[];
  chargers: DevicePayload[];
}

export interface LinkDeviceResponse {
  service_type: string;
  service_code: string;
  count: number;
  devices: Array<{ uuid: string }>;
  message?: string;
}

// ─── Auth API ───────────────────────────────────────────────────────────

/** POST /auth/nostr/generate — create a new Nostr identity, server sets cookie. */
export function generateIdentity(): Promise<GenerateIdentityResponse> {
  return post<GenerateIdentityResponse>('/auth/nostr/generate');
}

/** POST /auth/nostr/nsec-login — log in with an existing nsec, server sets cookie. */
export function nsecLogin(nsec: string): Promise<NpubIdentity> {
  return post<NpubIdentity>('/auth/nostr/nsec-login', { nsec });
}

/** POST /auth/nostr/logout — clear the session cookie. */
export function logout(): Promise<Record<string, never>> {
  return post<Record<string, never>>('/auth/nostr/logout');
}

// ─── Devices API ────────────────────────────────────────────────────────

/** GET /me/devices — list all chargers linked to the Nostr identity. */
export function listDevices(): Promise<DevicesListResponse> {
  return request<DevicesListResponse>('/me/devices');
}

/** POST /me/devices — link a charger via service credentials. */
export async function linkDevice(input: {
  service_code: string;
  username: string;
  password: string;
}): Promise<LinkDeviceResponse> {
  const res = await fetch(`${BASE}/me/devices`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = await res.json();
  if (!res.ok || !body.success) {
    throw new ApiError(body.message || body.error || `Link failed (${res.status})`, undefined, res.status);
  }
  return { ...body.data, message: body.message };
}

/** DELETE /me/devices/:uuid — unlink a charger. */
export function unlinkDevice(uuid: string): Promise<{ uuid: string }> {
  return del<{ uuid: string }>(`/me/devices/${encodeURIComponent(uuid)}`);
}

// ─── Actions API ────────────────────────────────────────────────────────

/** POST /me/device/virtual/:uuid/start — start a virtual device. */
export function startVirtual(uuid: string): Promise<unknown> {
  return post(`/me/device/virtual/${encodeURIComponent(uuid)}/start`);
}

/** POST /me/device/virtual/:uuid/stop — stop a virtual device. */
export function stopVirtual(uuid: string): Promise<unknown> {
  return post(`/me/device/virtual/${encodeURIComponent(uuid)}/stop`);
}

/** POST /me/devices/:uuid/action/:action — execute action on any linked device (Nostr auth). */
export function executeAction(uuid: string, action: string): Promise<unknown> {
  return post(`/me/devices/${encodeURIComponent(uuid)}/action/${encodeURIComponent(action)}`);
}

/** GET /me/device — legacy full dashboard data (charger status, sessions). */
export function getDashboard(): Promise<unknown> {
  return request<unknown>('/me/device');
}

// ─── nsec persistence ───────────────────────────────────────────────────

export function getStoredNsec(): string | null {
  try {
    return localStorage.getItem(NSEC_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeNsec(nsec: string): void {
  try {
    localStorage.setItem(NSEC_STORAGE_KEY, nsec);
  } catch {
    /* localStorage may be unavailable (private mode); cookie still authenticates */
  }
}

export function clearStoredNsec(): void {
  try {
    localStorage.removeItem(NSEC_STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
